import React from 'react';
import { Link } from 'react-router-dom';
import { useCMS } from '../context/CMSContext';
import SEO from '../components/SEO';

export default function Prices() {
  const { getPage } = useCMS();
  const page = getPage('priser');

  const metaTitle = page?.meta_title?.da || 'Priser - EquiGramVet | Kiropraktik til heste og hunde';
  const metaDesc = page?.meta_description?.da || 'Se priser på veterinær kiropraktik til heste og hunde hos EquiGramVet. Opstarts særtilbud resten af 2026, kørsel og afbudspolitik.';

  return (
    <>
      <SEO 
        title={metaTitle}
        description={metaDesc}
      />

      {/* Header Sektion */}
      <section className="bg-brand-beige/40 border-b border-brand-sand/30 py-12 md:py-16">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-sand/50 border border-brand-warm/40 text-brand-brown text-xs font-semibold uppercase tracking-wider mb-4 shadow-xs">
            <i className="fa-regular fa-tag text-brand-dark" /> Opstarts særtilbud resten af 2026
          </div>
          <h1 className="text-3xl md:text-5xl font-serif font-bold text-brand-dark">
            Priser & Særtilbud
          </h1>
          <p className="text-brand-brown text-sm md:text-base mt-4 max-w-2xl mx-auto leading-relaxed">
            Gennemskuelige priser på professionel kiropraktisk behandling af heste og hunde. 
            Alle priser er inkl. moms.
          </p>
        </div>
      </section>

      {/* Hovedsektion: Priskort */}
      <section className="py-16 md:py-24 bg-brand-cream">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
            
            {/* HEST PRISKORT */}
            <div className="rounded-brand bg-brand-beige/30 border-2 border-brand-sand/70 p-8 sm:p-10 flex flex-col justify-between relative shadow-sm hover:border-brand-brown/40 transition-all duration-200">
              <div className="absolute top-6 right-6">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-sand/60 text-brand-dark text-xs font-semibold uppercase tracking-wide">
                  <i className="fa-regular fa-horse" /> Hest
                </span>
              </div>

              <div>
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-brown block mb-2">
                  Kiropraktik &bull; Hest
                </span>
                <h2 className="text-2xl sm:text-3xl font-serif font-bold text-brand-dark mb-4">
                  Behandling af hest
                </h2>
                <p className="text-brand-brown text-sm leading-relaxed mb-8">
                  Resten af 2026 køres opstarts særtilbud med 899.- for første behandling af hest, 20% rabat ved efterfølgende hest i samme stald. Opfølgende behandling koster 499.- , 20% igen på efterfølgende hest i samme stald. Priser er inkl. moms.
                </p>

                {/* Prisdetaljer */}
                <div className="space-y-4 pt-2">
                  {/* Første behandling */}
                  <div className="p-4 rounded-brand bg-brand-cream border border-brand-sand/50">
                    <div className="flex items-baseline justify-between gap-4">
                      <div>
                        <h3 className="font-serif font-bold text-base text-brand-dark">Første behandling</h3>
                        <p className="text-xs text-brand-brown mt-0.5">Opstarts særtilbud resten af 2026</p>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-serif font-bold text-brand-dark">899.-</span>
                        <span className="text-xs text-brand-brown block">inkl. moms</span>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-brand-sand/30 flex items-center gap-2 text-xs text-brand-dark font-medium">
                      <i className="fa-regular fa-badge-percent text-brand-brown" />
                      <span><strong>20% rabat</strong> ved efterfølgende hest i samme stald</span>
                    </div>
                  </div>

                  {/* Opfølgende behandling */}
                  <div className="p-4 rounded-brand bg-brand-cream border border-brand-sand/50">
                    <div className="flex items-baseline justify-between gap-4">
                      <div>
                        <h3 className="font-serif font-bold text-base text-brand-dark">Opfølgende behandling</h3>
                        <p className="text-xs text-brand-brown mt-0.5">Gælder opfølgende konsultationer</p>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-serif font-bold text-brand-dark">499.-</span>
                        <span className="text-xs text-brand-brown block">inkl. moms</span>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-brand-sand/30 flex items-center gap-2 text-xs text-brand-dark font-medium">
                      <i className="fa-regular fa-badge-percent text-brand-brown" />
                      <span><strong>20% igen</strong> på efterfølgende hest i samme stald</span>
                    </div>
                  </div>
                </div>

                {/* Yderligere information */}
                <div className="mt-6 space-y-2 text-xs text-brand-brown">
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-brand-brown/70 text-[10px]" />
                    <span>Grundig anamnese og gennemgang af hestens bevægelsesmønster</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-brand-brown/70 text-[10px]" />
                    <span>Palpation af rygsøjle, bækken og ekstremiteter</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-brand-brown/70 text-[10px]" />
                    <span>Skånsom, målrettet kiropraktisk justering</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-brand-brown/70 text-[10px]" />
                    <span>Rådgivning og øvelser til ejeren</span>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-brand-sand/50">
                <Link
                  to="/kontakt"
                  className="block w-full text-center py-3.5 px-6 bg-brand-dark text-brand-cream font-medium rounded-brand hover:bg-brand-brown transition-colors duration-200 shadow-sm"
                >
                  Book tid til hest
                </Link>
              </div>
            </div>

            {/* HUND PRISKORT */}
            <div className="rounded-brand bg-brand-beige/30 border-2 border-brand-sand/70 p-8 sm:p-10 flex flex-col justify-between relative shadow-sm hover:border-brand-brown/40 transition-all duration-200">
              <div className="absolute top-6 right-6">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-sand/60 text-brand-dark text-xs font-semibold uppercase tracking-wide">
                  <i className="fa-regular fa-dog" /> Hund
                </span>
              </div>

              <div>
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-brown block mb-2">
                  Kiropraktik &bull; Hund
                </span>
                <h2 className="text-2xl sm:text-3xl font-serif font-bold text-brand-dark mb-4">
                  Behandling af hund
                </h2>
                <p className="text-brand-brown text-sm leading-relaxed mb-8">
                  Hunde særtilbud på 399.- første behandling resten af 2026, opfølgende 249.-. Samme sag med 20% rabat ved ekstra hund samme sted. Priser er inkl. moms.
                </p>

                {/* Prisdetaljer */}
                <div className="space-y-4 pt-2">
                  {/* Første behandling */}
                  <div className="p-4 rounded-brand bg-brand-cream border border-brand-sand/50">
                    <div className="flex items-baseline justify-between gap-4">
                      <div>
                        <h3 className="font-serif font-bold text-base text-brand-dark">Første behandling</h3>
                        <p className="text-xs text-brand-brown mt-0.5">Særtilbud resten af 2026</p>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-serif font-bold text-brand-dark">399.-</span>
                        <span className="text-xs text-brand-brown block">inkl. moms</span>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-brand-sand/30 flex items-center gap-2 text-xs text-brand-dark font-medium">
                      <i className="fa-regular fa-badge-percent text-brand-brown" />
                      <span><strong>20% rabat</strong> ved ekstra hund samme sted</span>
                    </div>
                  </div>

                  {/* Opfølgende behandling */}
                  <div className="p-4 rounded-brand bg-brand-cream border border-brand-sand/50">
                    <div className="flex items-baseline justify-between gap-4">
                      <div>
                        <h3 className="font-serif font-bold text-base text-brand-dark">Opfølgende behandling</h3>
                        <p className="text-xs text-brand-brown mt-0.5">Gælder opfølgende konsultationer</p>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-serif font-bold text-brand-dark">249.-</span>
                        <span className="text-xs text-brand-brown block">inkl. moms</span>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-brand-sand/30 flex items-center gap-2 text-xs text-brand-dark font-medium">
                      <i className="fa-regular fa-badge-percent text-brand-brown" />
                      <span><strong>20% rabat</strong> ved ekstra hund samme sted</span>
                    </div>
                  </div>
                </div>

                {/* Yderligere information */}
                <div className="mt-6 space-y-2 text-xs text-brand-brown">
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-brand-brown/70 text-[10px]" />
                    <span>Rolig og tryg undersøgelse på hundens egne præmisser</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-brand-brown/70 text-[10px]" />
                    <span>Undersøgelse af rygsøjle, nakke og led for bevægelsesindskrænkninger</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-brand-brown/70 text-[10px]" />
                    <span>Nænsom kiropraktisk justering og ledmobilisering</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-brand-brown/70 text-[10px]" />
                    <span>Vejledning i skånsom genoptræning og hjemmeøvelser</span>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-brand-sand/50">
                <Link
                  to="/kontakt"
                  className="block w-full text-center py-3.5 px-6 bg-brand-dark text-brand-cream font-medium rounded-brand hover:bg-brand-brown transition-colors duration-200 shadow-sm"
                >
                  Book tid til hund
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Praktisk information & Betingelser */}
      <section className="py-16 md:py-20 bg-brand-beige/20 border-t border-brand-sand/40">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-brown mb-2 block">
              Vilkår & Kørsel
            </span>
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-brand-dark">
              Praktisk information
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* Kørsel */}
            <div className="p-6 rounded-brand bg-brand-cream border border-brand-sand/60 shadow-xs flex flex-col justify-between">
              <div>
                <div className="w-10 h-10 rounded-full bg-brand-beige flex items-center justify-center text-brand-dark mb-4">
                  <i className="fa-regular fa-car" />
                </div>
                <h3 className="font-serif font-bold text-lg text-brand-dark mb-3">
                  Kørsel
                </h3>
                <p className="text-brand-brown text-sm leading-relaxed">
                  Kørsel: statens kilometer takst + Moms , men takseres først fra 10 km fra hjemmeadresse. Ved flere i stalden deles kørslen mellem klienterne.
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-brand-sand/30 text-xs text-brand-brown/80 font-medium">
                Udgangspunkt: Borup, Sjælland
              </div>
            </div>

            {/* Afbudspolitik */}
            <div className="p-6 rounded-brand bg-brand-cream border border-brand-sand/60 shadow-xs flex flex-col justify-between">
              <div>
                <div className="w-10 h-10 rounded-full bg-brand-beige flex items-center justify-center text-brand-dark mb-4">
                  <i className="fa-regular fa-calendar-xmark" />
                </div>
                <h3 className="font-serif font-bold text-lg text-brand-dark mb-3">
                  Afbudspolitik
                </h3>
                <p className="text-brand-brown text-sm leading-relaxed">
                  Afbudspolitik: ved mindre end 24 timer før takseres 50% af konsultationen.
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-brand-sand/30 text-xs text-brand-brown/80 font-medium">
                Afbud meldes telefonisk eller via email
              </div>
            </div>

            {/* Prisjustering */}
            <div className="p-6 rounded-brand bg-brand-cream border border-brand-sand/60 shadow-xs flex flex-col justify-between">
              <div>
                <div className="w-10 h-10 rounded-full bg-brand-beige flex items-center justify-center text-brand-dark mb-4">
                  <i className="fa-regular fa-clock-rotate-left" />
                </div>
                <h3 className="font-serif font-bold text-lg text-brand-dark mb-3">
                  Prisjustering
                </h3>
                <p className="text-brand-brown text-sm leading-relaxed">
                  Prisjustering Per 1/1/2027
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-brand-sand/30 text-xs text-brand-brown/80 font-medium">
                Særtilbuddene gælder hele 2026
              </div>
            </div>

          </div>

          {/* Betaling og spørgsmål boks */}
          <div className="mt-10 p-6 rounded-brand bg-brand-beige/40 border border-brand-sand/60 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-brand-sand/60 flex items-center justify-center text-brand-dark shrink-0 mt-0.5">
                <i className="fa-regular fa-circle-info" />
              </div>
              <div>
                <h4 className="font-serif font-bold text-base text-brand-dark">
                  Spørgsmål til priser eller samlet aftale?
                </h4>
                <p className="text-brand-brown text-xs sm:text-sm mt-1 leading-relaxed">
                  Har I flere heste i samme stald eller spørgsmål til kørsel og ruter, er du altid velkommen til at kontakte os for en uforpligtende snak.
                </p>
              </div>
            </div>
            <Link
              to="/kontakt"
              className="whitespace-nowrap px-5 py-2.5 bg-brand-dark text-brand-cream text-xs font-semibold uppercase tracking-wider rounded-brand hover:bg-brand-brown transition-colors duration-200"
            >
              Kontakt os
            </Link>
          </div>

        </div>
      </section>
    </>
  );
}
